// apps/dashboard/src/components/TrialBanner.tsx
// Persistent banner shown to tenants in TRIAL status.
// Displays days remaining and a CTA to open the UpgradeModal.
// SPEC: docs/specs/tenant/sprint-ak-self-serve-trial.spec.md §7.1

import { Button } from '@ominiflow/ui';
import { AlertTriangle, X } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface TrialBannerProps {
  trialEndsAt: string; // ISO 8601
  onUpgradeClick: () => void;
}

function getDaysLeft(trialEndsAt: string): number {
  const end = new Date(trialEndsAt).getTime();
  return Math.ceil((end - Date.now()) / 86_400_000);
}

export function TrialBanner({ trialEndsAt, onUpgradeClick }: TrialBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const daysLeft = getDaysLeft(trialEndsAt);

  useEffect(() => {
    const key = 'trial-banner-dismissed';
    if (sessionStorage.getItem(key) === '1') setDismissed(true);
  }, []);

  if (dismissed) return null;

  const isUrgent = daysLeft <= 3;

  const bannerCls = isUrgent
    ? 'bg-red-50 border-red-200 text-red-900'
    : 'bg-amber-50 border-amber-200 text-amber-900';

  const handleDismiss = () => {
    sessionStorage.setItem('trial-banner-dismissed', '1');
    setDismissed(true);
  };

  const label =
    daysLeft <= 0
      ? 'Seu trial expirou.'
      : daysLeft === 1
        ? 'Seu trial expira hoje.'
        : `Seu trial expira em ${daysLeft} dias.`;

  return (
    <div
      role="alert"
      className={`flex items-center justify-between gap-3 border-b px-4 py-2 text-sm ${bannerCls}`}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{label}</span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={isUrgent ? 'destructive' : 'default'}
          className="h-7 text-xs"
          onClick={onUpgradeClick}
        >
          Fazer upgrade agora →
        </Button>
        <button
          type="button"
          aria-label="Fechar banner"
          className="p-1 rounded hover:bg-black/10 transition-colors"
          onClick={handleDismiss}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
